import { useState } from 'react';
import type { Client } from '@salonomy/types';
import { Button, Field, Modal, TextArea, TextInput } from '@salonomy/ui';
import { useCreateClient, useUpdateClient } from '../../data/clients.js';
import { useToast } from '../../lib/toast.js';

export function ClientModal({ existing, onClose }: { existing?: Client; onClose: () => void }) {
  const create = useCreateClient();
  const update = useUpdateClient();
  const toast = useToast();
  const editing = Boolean(existing);

  const [firstName, setFirstName] = useState(existing?.firstName ?? '');
  const [lastName, setLastName] = useState(existing?.lastName ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [colourFormula, setColourFormula] = useState(existing?.colourFormula ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [marketingConsent, setMarketingConsent] = useState(existing?.marketingConsent ?? false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!firstName.trim()) {
      toast.error('A first name is required.');
      return;
    }
    setBusy(true);
    const body = {
      firstName: firstName.trim(),
      lastName: lastName.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      colourFormula: colourFormula.trim() || null,
      notes: notes.trim() || null,
      marketingConsent,
    };
    try {
      if (editing && existing) {
        await update.mutateAsync({ id: existing.id, body });
        toast.success('Client updated');
      } else {
        await create.mutateAsync(body);
        toast.success('Client added');
      }
      onClose();
    } catch {
      toast.error('Could not save the client.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit client' : 'New client'}
      width={480}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save' : 'Add client'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="First name">
          <TextInput autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name">
          <TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Mobile">
          <TextInput
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+44 7700 900123"
          />
        </Field>
        <Field label="Email">
          <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
      <Field label="Colour formula" hint='e.g. "7.3 + 6% · 35 min"'>
        <TextInput value={colourFormula} onChange={(e) => setColourFormula(e.target.value)} />
      </Field>
      <Field label="Notes">
        <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}>
        <input
          type="checkbox"
          checked={marketingConsent}
          onChange={(e) => setMarketingConsent(e.target.checked)}
        />
        Marketing consent (email + SMS campaigns)
      </label>
    </Modal>
  );
}
